/**
 * Landing Page - MasterClass-inspired hero and featured content
 */
import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Navigation } from '../../components/layout/Navigation';
import { Footer } from '../../components/layout/Footer';
import { Container } from '../../components/ui/Container';
import { Button } from '../../components/ui/Button';
import { CourseCard } from '../../components/course/CourseCard';
import { Spinner } from '../../components/ui/Spinner';
import { getPage } from '../../service/app/app.service';
import type { Course } from '../../types/models';

/**
 * Home page config type
 */
type HomePageConfig = {
  bgVid?: string;
  heroFallbackImage?: string;
  mpbLogo?: string;
  programmingLanguages?: string[];
};

type BootcampApiItem = Readonly<{
  id: string;
  title: string;
  description: string;
  shortDescription?: string;
  thumbnail: string;
  trailerUrl?: string;
  paymentLink?: string;
  payment_link?: string;
  stripePaymentLink?: string;
  stripe_payment_link?: string;
  checkoutUrl?: string;
  checkout_url?: string;
  instructor?: Readonly<{
    profileImg?: string;
    name?: string;
  }>;
  level?: string;
  language?: string;
  topics?: ReadonlyArray<string>;
  duration?: number;
  lessonsCount?: number;
  studentsCount?: number;
  rating?: number;
  reviewsCount?: number;
  isPublished?: boolean;
  createdAt?: string;
  updatedAt?: string;
}>;

type BootcampPageData = Readonly<{
  bootcamps?: ReadonlyArray<BootcampApiItem>;
  data?: Readonly<{
    bootcamps?: ReadonlyArray<BootcampApiItem>;
  }>;
}>;

const isCourseLevel = (value?: string): value is Course['level'] =>
  value === 'beginner' || value === 'intermediate' || value === 'advanced' || value === 'all-levels';

const toCourse = (item: BootcampApiItem): Course => {
  const instructorName = item.instructor?.name || 'Bootcamp Instructor';
  const instructorSlug = instructorName.toLowerCase().replace(/[^a-z0-9]+/g, '-');
  const now = new Date().toISOString();

  const paymentLink =
    item.paymentLink ||
    item.payment_link ||
    item.stripePaymentLink ||
    item.stripe_payment_link ||
    item.checkoutUrl ||
    item.checkout_url;

  return {
    id: item.id,
    title: item.title,
    description: item.description,
    shortDescription: item.shortDescription || item.description,
    thumbnail: item.thumbnail,
    trailerUrl: item.trailerUrl,
    paymentLink,
    instructor: {
      id: `inst-${instructorSlug}`,
      name: instructorName,
      title: 'Bootcamp Instructor',
      bio: `${instructorName} leads this coding bootcamp track.`,
      avatar: item.instructor?.profileImg || 'https://images.unsplash.com/photo-1535713875002-d1d0cf377fde?w=200',
      expertise: item.topics || [],
      coursesCount: 1,
    },
    level: isCourseLevel(item.level) ? item.level : 'beginner',
    language: item.language || 'javascript',
    topics: item.topics || [],
    duration: item.duration || 0,
    lessonsCount: item.lessonsCount || 0,
    studentsCount: item.studentsCount || 0,
    rating: item.rating || 0,
    reviewsCount: item.reviewsCount || 0,
    isPublished: item.isPublished ?? true,
    createdAt: item.createdAt || now,
    updatedAt: item.updatedAt || now,
  };
};

/**
 * Hero section component
 */
const Hero: React.FC<{ config?: HomePageConfig }> = ({ config }) => {
  const navigate = useNavigate();
  const [isVideoAvailable, setIsVideoAvailable] = React.useState(true);
  
  // Fallback values if config is not loaded
  const bgVid = config?.bgVid || 'https://mpb-site.nyc3.digitaloceanspaces.com/vids/black-women-group-coder.mp4';
  const heroFallbackImage = config?.heroFallbackImage || 'https://images.unsplash.com/photo-1521587760476-6c12a4b040da?auto=format&fit=crop&w=2200&q=80';
  const mpbLogo = config?.mpbLogo;
  const programmingLanguages = config?.programmingLanguages || [];
  
  return (
    <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
      {/* Background media */}
      <div className="absolute inset-0 z-0">
        <img
          src={heroFallbackImage}
          alt="Coding bootcamp students"
          className="w-full h-full object-cover"
        />
        <video
          className={`w-full h-full object-cover absolute inset-0 ${isVideoAvailable ? 'opacity-100' : 'opacity-0'}`}
          autoPlay
          loop
          muted
          playsInline
          onError={() => setIsVideoAvailable(false)}
        >
          <source src={bgVid} type="video/mp4" />
        </video>
      </div>
      
      {/* Layered overlays for readability */}
      <div className="absolute inset-0 z-10 bg-brand-blue/65" />
      <div className="absolute inset-0 z-10 bg-gradient-to-br from-brand-blue/90 via-brand-blue/70 to-brand-blue/95" />
      <div className="absolute inset-0 z-10 bg-[radial-gradient(circle_at_20%_20%,rgba(55,255,139,0.15),transparent_40%),radial-gradient(circle_at_80%_80%,rgba(157,122,27,0.25),transparent_40%)]" />
      
      {/* Content */}
      <Container className="relative z-20 text-center">
        <div className="max-w-4xl mx-auto bg-brand-blue/40 border border-brand-gold/40 rounded-2xl px-6 py-10 md:px-12 md:py-14 backdrop-blur-sm shadow-2xl">
          {mpbLogo && (
            <img
              src={mpbLogo}
              alt="Master Polyglots Bootcamp"
              className="h-20 md:h-28 w-auto mx-auto mb-8 drop-shadow-[0_0_20px_rgba(157,122,27,0.6)] hover:drop-shadow-[0_0_30px_rgba(157,122,27,0.8)] transition-all duration-300"
            />
          )}
          <h1 className="text-display-lg md:text-display-xl font-bold text-white mb-6 animate-fade-in">
            Master Software Engineering with{' '}
            <span className="text-brand-gold">World-Class</span> Instructors
          </h1>
          <p className="text-xl md:text-2xl text-gray-300 mb-10 animate-slide-up">
            Learn full-stack development through premium coding bootcamps built for
            beginners, career switchers, and future software engineers.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center animate-slide-up">
            <Button
              variant="gold"
              size="lg"
              onClick={() => navigate('/courses')}
            >
              Explore Courses
            </Button>
            <Button
              variant="outline"
              size="lg"
              onClick={() => navigate('/courses')}
            >
              View Courses
            </Button>
          </div>
          {programmingLanguages.length > 0 && (
            <div className="mt-10 flex flex-wrap items-center justify-center gap-4 md:gap-5">
              {programmingLanguages.map((logo, index) => (
                <div
                  key={`${logo}-${index}`}
                  className="w-11 h-11 md:w-12 md:h-12 bg-white/95 rounded-lg p-2 shadow-lg"
                >
                  <img src={logo} alt="Programming language logo" className="w-full h-full object-cover" />
                </div>
              ))}
            </div>
          )}
        </div>
      </Container>
    </section>
  );
};

/**
 * Featured courses section
 */
const FeaturedCourses: React.FC = () => {
  const { data, isLoading, error } = useQuery({
    queryKey: ['courses', 'featured'],
    queryFn: async () => {
      const result = await getPage<BootcampPageData>('bootcamp');
      if (result.error || !result.data) {
        throw new Error(
          result.error instanceof Error ? result.error.message : String(result.error)
        );
      }

      const raw = result.data.bootcamps || result.data.data?.bootcamps || [];
      return raw.map(toCourse).slice(0, 6);
    },
  });
  
  if (isLoading) {
    return (
      <div className="flex justify-center py-20">
        <Spinner size="lg" />
      </div>
    );
  }
  
  if (error) {
    return (
      <div className="text-center py-20 text-gray-400">
        Failed to load featured courses
      </div>
    );
  }
  
  return (
    <section className="py-20 bg-brand-dark">
      <Container>
        <div className="text-center mb-12">
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">
            Featured Courses
          </h2>
          <p className="text-xl text-gray-400">
            Start your software engineering journey today
          </p>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {data?.slice(0, 6).map((course) => (
            <CourseCard key={course.id} course={course} />
          ))}
        </div>
        
        <div className="text-center mt-12">
          <Link to="/courses">
            <Button variant="outline" size="lg">
              View All Courses
            </Button>
          </Link>
        </div>
      </Container>
    </section>
  );
};

/**
 * Instructor highlight section
 */
const InstructorHighlight: React.FC = () => {
  return (
    <section className="py-20 bg-brand-gray-900">
      <Container>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          {/* Image */}
          <div className="relative">
            <div className="aspect-square rounded-2xl overflow-hidden">
              <img
                src="https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?w=800"
                alt="Instructor"
                className="w-full h-full object-cover"
              />
            </div>
            <div className="absolute -bottom-6 -right-6 bg-brand-gold text-brand-dark px-8 py-4 rounded-lg shadow-xl">
              <p className="text-4xl font-bold">20+</p>
              <p className="text-sm font-semibold">Expert Instructors</p>
            </div>
          </div>
          
          {/* Content */}
          <div>
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
              Learn from Expert <span className="text-brand-gold">Engineers</span>
            </h2>
            <p className="text-xl text-gray-300 mb-6">
              Our instructors are senior developers and technical mentors with real-world
              experience building and scaling production software.
            </p>
            <ul className="space-y-4 mb-8">
              {[
                'Proven project-based teaching methodology',
                'Live code reviews and pair programming sessions',
                'Portfolio-ready full-stack projects',
                'Personalized developer growth plans',
              ].map((feature, index) => (
                <li key={index} className="flex items-center text-gray-300">
                  <span className="text-brand-gold mr-3">✓</span>
                  {feature}
                </li>
              ))}
            </ul>
            <Link to="/courses">
              <Button variant="gold" size="lg">
                Start Learning
              </Button>
            </Link>
          </div>
        </div>
      </Container>
    </section>
  );
};

/**
 * Testimonials section
 */
const Testimonials: React.FC = () => {
  const testimonials = [
    {
      id: '1',
      name: 'Sarah Johnson',
      role: 'Frontend Developer',
      image: 'https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=200',
      content: 'Master Polyglots helped me transition into tech with a portfolio that got me hired in under 4 months.',
    },
    {
      id: '2',
      name: 'Michael Chen',
      role: 'Backend Engineer',
      image: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=200',
      content: 'The curriculum was practical and deep. I now build production APIs with confidence.',
    },
    {
      id: '3',
      name: 'Emily Rodriguez',
      role: 'Full-Stack Developer',
      image: 'https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=200',
      content: 'Best investment I made in my career. The mentorship and projects made the difference.',
    },
  ];
  
  return (
    <section className="py-20 bg-brand-dark">
      <Container>
        <div className="text-center mb-12">
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">
            Student Developer Success Stories
          </h2>
          <p className="text-xl text-gray-400">
            Join thousands of successful coding bootcamp graduates
          </p>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {testimonials.map((testimonial) => (
            <div
              key={testimonial.id}
              className="bg-brand-gray-800 p-8 rounded-xl"
            >
              <div className="flex items-center mb-6">
                <div>
                  <h4 className="text-white font-semibold">{testimonial.name}</h4>
                  <p className="text-gray-400 text-sm">{testimonial.role}</p>
                </div>
              </div>
              <p className="text-gray-300 italic">"{testimonial.content}"</p>
            </div>
          ))}
        </div>
      </Container>
    </section>
  );
};

/**
 * Landing page component
 */
export const LandingPage: React.FC = () => {
  // Fetch home page configuration from MCP agent
  const { data: homeConfig, isLoading: configLoading } = useQuery({
    queryKey: ['page', 'home'],
    queryFn: async () => {
      const result = await getPage<HomePageConfig>('home');
      if (result.error) {
        console.warn('Failed to load home config, using defaults:', result.error);
        return null;
      }
      return result.data;
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  return (
    <div className="min-h-screen bg-brand-dark">
      <Navigation />
      <main>
        {configLoading ? (
          <div className="min-h-screen flex items-center justify-center">
            <Spinner size="xl" />
          </div>
        ) : (
          <>
            <Hero config={homeConfig || undefined} />
            <FeaturedCourses />
            <InstructorHighlight />
            <Testimonials />
          </>
        )}
      </main>
      <Footer />
    </div>
  );
};
